import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import Scroll from 'src/game/components/shared/Scroll';
import { FriendItem, FriendItemParams } from '../components/FriendItem';
import FriendshipRequestsBadge from '../FriendshipRequestsBadge';
import { user } from 'src/StateObserver';
import {
  getFriendshipPendingRequests,
  getFriendshipPendingRequestsSent,
} from 'src/replicant/friends/friends.getters';
import { FriendshipRequest } from 'src/replicant/friends/friends.ruleset';
import { acceptFriendRequest, rejectFriendRequest } from '../friends.sequences';

type CloseHandler = () => void;

/**
 * Simplified friend requests popup for Add Friends V2 users.
 * Shows only the friend requests list without tabs or search functionality.
 */
export default class PopupFriendRequests extends PopupBasicLazy {
  private scroll: Scroll<FriendItemParams, FriendItem>;
  private noRequestLabel: LangBitmapFontTextView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 750,
      darkerBg: false,
      superview: opts.superview,
      close: opts.close,
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP,
      },
    });

    this.buttonClose.removeFromSuperview();

    // Badge near title area
    this.createBadge();

    // Scroll list for requests
    const contentY = 95;
    const contentHeight = 500;

    this.scroll = new Scroll({
      superview: this.box,
      recycle: true,
      createItem: (superview: View, _i: number, data: FriendItemParams) => {
        const item = new FriendItem({ superview });
        item.setup(this.getItemData(data));
        return item;
      },
      setData: (_i: number, data: FriendItemParams, item: FriendItem) => {
        item.setup(this.getItemData(data));
      },
      showBg: true,
      rect: {
        x: 30,
        y: contentY,
        width: this.box.style.width - 60,
        height: contentHeight,
      },
    });

    // Empty state message
    this.noRequestLabel = new LangBitmapFontTextView({
      superview: this.box,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Body',
      width: this.box.style.width - 60,
      height: 200,
      color: '#AE7E4A',
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: contentY + contentHeight / 2,
    });

    // Close button
    new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.cancel,
      labelOffsetY: -1,
      fontSize: 46,
      font: 'Body',
      x: this.box.style.width / 2,
      y: this.box.style.height - 80,
      width: 259,
      height: 100,
      centerOnOrigin: true,
      localeText: () => i18n('basic.close'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        opts.close();
      },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CLOSE,
      },
    });
  }

  public getAssetsGroup() {
    return null;
  }

  public init() {
    super.init({
      title: i18n('friendsAction.friendRequests'),
    });
    this.refresh();
  }

  private createBadge() {
    new FriendshipRequestsBadge({
      superview: this.box,
      size: 40,
      x: this.box.style.width - 60,
      y: 25,
      font: 'Body',
      fontSize: 18,
      textColor: 'white',
      style: 'red',
    });
  }

  private getRequest(id: string): FriendshipRequest | undefined {
    return user().friendshipRequests[id] as FriendshipRequest | undefined;
  }

  private getItemData(params: FriendItemParams): FriendItemParams {
    return {
      ...params,
      onAccept: async () => {
        const request = this.getRequest(params.id);
        if (!request) return;
        const ok = await acceptFriendRequest(request, 'hud');
        if (ok) this.refresh();
      },
      onReject: async () => {
        const request = this.getRequest(params.id);
        if (!request) return;
        const ok = await rejectFriendRequest(request, 'hud');
        if (ok) this.refresh();
      },
    };
  }

  private mapProfileToParams(
    profile: FriendshipRequest['owner'],
    requestSent = false,
  ): FriendItemParams {
    return {
      id: profile.id,
      name: profile.name,
      profilePicture: profile.profilePicture,
      frame: profile.frame,
      species: profile.species,
      requestSent,
    };
  }

  public refresh() {
    const requestsReceived = getFriendshipPendingRequests(user()).map(
      (request) => this.mapProfileToParams(request.owner),
    );

    const requestsSent = getFriendshipPendingRequestsSent(user()).map(
      (request) => this.mapProfileToParams(request.target, true),
    );

    const items = [...requestsReceived, ...requestsSent];

    this.scroll.setItems(items);

    const hasNoRequests = items.length === 0;
    this.noRequestLabel.updateOpts({ visible: hasNoRequests });
    if (hasNoRequests) {
      this.noRequestLabel.localeText = () => i18n('friends.noRequests');
    }
  }
}
